/**
 * ImageService for handling tarot card image loading and caching
 * Maps card names to their respective image paths with lazy loading support
 */

export type Suit = 'cups' | 'pentacles' | 'swords' | 'wands';
export type Court = 'page' | 'knight' | 'queen' | 'king';

interface CardImageCache {
  [key: string]: string;
}

export class ImageService {
  private static instance: ImageService;
  private imageCache: CardImageCache = {};
  private loadingPromises: { [key: string]: Promise<string> } = {};

  private constructor() {}

  public static getInstance(): ImageService {
    if (!ImageService.instance) {
      ImageService.instance = new ImageService();
    }
    return ImageService.instance;
  }

  /**
   * Major Arcana cards mapping (0-21)
   * The Fool is 0, The World is 21
   */
  private readonly majorArcanaMap: { [key: string]: number } = {
    'The Fool': 0,
    'The Magician': 1,
    'The High Priestess': 2,
    'The Empress': 3,
    'The Emperor': 4,
    'The Hierophant': 5,
    'The Lovers': 6,
    'The Chariot': 7,
    'Strength': 8,
    'The Hermit': 9,
    'Wheel of Fortune': 10,
    'Justice': 11,
    'The Hanged Man': 12,
    'Death': 13,
    'Temperance': 14,
    'The Devil': 15,
    'The Tower': 16,
    'The Star': 17,
    'The Moon': 18,
    'The Sun': 19,
    'Judgement': 20,
    'The World': 21
  };

  /**
   * Get the image path for a tarot card
   * @param cardName - The name of the card
   * @returns The path to the card image
   */
  public getCardImagePath(cardName: string): string {
    // Check if it's a major arcana card
    if (this.majorArcanaMap.hasOwnProperty(cardName)) {
      const cardNumber = this.majorArcanaMap[cardName];
      return `/cards/major/major-${cardNumber}.jpg`;
    }

    // Check if it's a minor arcana card
    const minorPath = this.parseMinorArcanaCard(cardName);
    if (minorPath) {
      return minorPath;
    }

    // Fallback to a default card back image
    return '/cards/card-back.jpg';
  }

  /**
   * Parse minor arcana card names and return their image paths
   * @param cardName - The name of the minor arcana card
   * @returns The path to the card image or null if not a minor arcana card
   */
  private parseMinorArcanaCard(cardName: string): string | null {
    const suits = ['Cups', 'Pentacles', 'Swords', 'Wands'];
    const numbers = ['Ace', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
    const courts = ['Page', 'Knight', 'Queen', 'King'];

    // Check for numbered cards (Ace to Ten)
    for (let i = 0; i < numbers.length; i++) {
      for (const suit of suits) {
        if (cardName === `${numbers[i]} of ${suit}`) {
          const suitName = suit.toLowerCase();
          const cardNumber = i + 1; // Ace = 1, Two = 2, etc.
          return `/cards/minor/${suitName}-${cardNumber}.jpg`;
        }
      }
    }

    // Check for court cards
    for (let i = 0; i < courts.length; i++) {
      for (const suit of suits) {
        if (cardName === `${courts[i]} of ${suit}`) {
          const suitName = suit.toLowerCase();
          const courtNumber = i + 11; // Page = 11, Knight = 12, Queen = 13, King = 14
          return `/cards/minor/${suitName}-${courtNumber}.jpg`;
        }
      }
    }

    return null;
  }

  /**
   * Preload a card image and cache it
   * @param cardName - The name of the card to preload
   * @returns Promise that resolves when the image is loaded
   */
  public async preloadCard(cardName: string): Promise<string> {
    const imagePath = this.getCardImagePath(cardName);

    // Return cached image if available
    if (this.imageCache[cardName]) {
      return this.imageCache[cardName];
    }

    // Return existing promise if already loading
    if (await this.loadingPromises[cardName]) {
      return this.loadingPromises[cardName];
    }

    // Create new loading promise
    this.loadingPromises[cardName] = new Promise((resolve) => {
      const img = new Image();
      
      img.onload = () => {
        this.imageCache[cardName] = imagePath;
        delete this.loadingPromises[cardName];
        resolve(imagePath);
      };
      
      img.onerror = () => {
        delete this.loadingPromises[cardName];
        // Fallback to card back on error
        const fallbackPath = '/cards/card-back.jpg';
        this.imageCache[cardName] = fallbackPath;
        resolve(fallbackPath);
      };
      
      img.src = imagePath;
    });

    return this.loadingPromises[cardName];
  }

  /**
   * Lazy load a card image with loading state
   * @param cardName - The name of the card to load
   * @returns Object with loading state and image path
   */
  public async useLazyCardImage(cardName: string): Promise<{ imagePath: string | null; isLoading: boolean; error: string | null; }> {
    // Return cached result immediately
    if (this.imageCache[cardName]) {
      return { 
        imagePath: this.imageCache[cardName], 
        isLoading: false, 
        error: null 
      };
    }

    // Return loading state if currently loading
    if (await this.loadingPromises[cardName]) {
      return { 
        imagePath: null, 
        isLoading: true, 
        error: null 
      };
    }

    // Start loading the image
    this.preloadCard(cardName).catch(error => {
      console.error(`Failed to load card image for ${cardName}:`, error);
    });

    return { 
      imagePath: null, 
      isLoading: true, 
      error: null 
    };
  }

  /**
   * Get all major arcana card names
   * @returns Array of all major arcana card names
   */
  public getAllMajorArcanaNames(): string[] {
    return Object.keys(this.majorArcanaMap);
  }

  /**
   * Generate all minor arcana card names
   * @returns Array of all minor arcana card names
   */
  public getAllMinorArcanaNames(): string[] {
    const suits = ['Cups', 'Pentacles', 'Swords', 'Wands'];
    const numbers = ['Ace', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
    const courts = ['Page', 'Knight', 'Queen', 'King'];
    
    const cards: string[] = [];
    
    // Add numbered cards
    for (const number of numbers) {
      for (const suit of suits) {
        cards.push(`${number} of ${suit}`);
      }
    }
    
    // Add court cards
    for (const court of courts) {
      for (const suit of suits) {
        cards.push(`${court} of ${suit}`);
      }
    }
    
    return cards;
  }

  /**
   * Clear the image cache
   */
  public clearCache(): void {
    this.imageCache = {};
    this.loadingPromises = {};
  }
}

// Export singleton instance
export const imageService = ImageService.getInstance();